import type {
  AppendExcelMappingRequest,
  AppendExcelParseResult,
  CreateExcelDataSourceFromUploadRequest,
  ExcelIngestSheetResult,
  ExcelParseResult,
  ExcelStreamEvent,
} from '@innermate/shared';
import { parseSseEventBlock } from './sse-parse';

export interface ExcelStreamCallbacks {
  onProgress?: (message: string) => void;
  onSheetStart?: (sheetName: string) => void;
  onSheetDone?: (ev: { sheetName: string; rowCount: number; isData: boolean }) => void;
  onIngestSheetDone?: (result: ExcelIngestSheetResult) => void;
  onHeartbeat?: (elapsedMs: number) => void;
  signal?: AbortSignal;
}

export interface ExcelParseStreamResult {
  parse: ExcelParseResult | null;
}

export interface ExcelAppendParseStreamResult {
  parse: AppendExcelParseResult | null;
}

export interface ExcelCreateStreamResult {
  dataSourceId: string | null;
  sheets: ExcelIngestSheetResult[];
  /** createSkill 옵션이 있었고 성공했으면 그 skill id. 없으면 null. */
  skillId: string | null;
}

async function readSse(res: Response, onEvent: (ev: ExcelStreamEvent) => void): Promise<void> {
  if (!res.ok || !res.body) {
    let message = `요청 실패 (${res.status})`;
    try {
      const text = await res.text();
      const json = text ? JSON.parse(text) : null;
      if (json?.error?.message) message = json.error.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    if (value) buf += value;
    const events = buf.split('\n\n');
    buf = events.pop() ?? '';
    for (const ev of events) {
      const json = parseSseEventBlock(ev);
      if (!json) continue;
      try {
        onEvent(JSON.parse(json) as ExcelStreamEvent);
      } catch {
        // ignore malformed
      }
    }
  }
}

function dispatchCommonCallbacks(payload: ExcelStreamEvent, cb: ExcelStreamCallbacks): void {
  switch (payload.type) {
    case 'progress':
      cb.onProgress?.(payload.message);
      break;
    case 'sheet_start':
      cb.onSheetStart?.(payload.sheetName);
      break;
    case 'sheet_done':
      cb.onSheetDone?.({
        sheetName: payload.sheetName,
        rowCount: payload.rowCount,
        isData: payload.isData,
      });
      break;
    case 'ingest_sheet_done':
      cb.onIngestSheetDone?.(payload.result);
      break;
    case 'heartbeat':
      cb.onHeartbeat?.(payload.elapsedMs);
      break;
    case 'error':
      throw new Error(payload.message);
  }
}

/** POST /api/admin/data-sources/excel/parse — fileUploadId 로 파싱 + ExcelParseResult 반환. */
export async function streamExcelParse(
  fileUploadId: string,
  callbacks: ExcelStreamCallbacks = {},
): Promise<ExcelParseStreamResult> {
  const res = await fetch('/api/admin/data-sources/excel/parse', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ fileUploadId }),
    signal: callbacks.signal,
  });
  let parse: ExcelParseResult | null = null;
  await readSse(res, (ev) => {
    if (ev.type === 'parse_done') {
      parse = ev.result;
      return;
    }
    dispatchCommonCallbacks(ev, callbacks);
  });
  return { parse };
}

/** POST /api/admin/data-sources/excel/from-upload — 데이터소스 생성 commit. */
export async function streamExcelCreate(
  body: CreateExcelDataSourceFromUploadRequest,
  callbacks: ExcelStreamCallbacks = {},
): Promise<ExcelCreateStreamResult> {
  const res = await fetch('/api/admin/data-sources/excel/from-upload', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal: callbacks.signal,
  });
  let dataSourceId: string | null = null;
  let sheets: ExcelIngestSheetResult[] = [];
  let skillId: string | null = null;
  await readSse(res, (ev) => {
    if (ev.type === 'done') {
      dataSourceId = ev.dataSourceId;
      sheets = ev.sheets;
      skillId = ev.skillId;
      return;
    }
    dispatchCommonCallbacks(ev, callbacks);
  });
  return { dataSourceId, sheets, skillId };
}

/** POST /api/admin/data-sources/:id/excel/parse — 추가 업로드 parse + autoMapping. */
export async function streamExcelAppendParse(
  dataSourceId: string,
  fileUploadId: string,
  callbacks: ExcelStreamCallbacks = {},
): Promise<ExcelAppendParseStreamResult> {
  const res = await fetch(`/api/admin/data-sources/${dataSourceId}/excel/parse`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ fileUploadId }),
    signal: callbacks.signal,
  });
  let parse: AppendExcelParseResult | null = null;
  await readSse(res, (ev) => {
    if (ev.type === 'append_parse_done') {
      parse = ev.result;
      return;
    }
    dispatchCommonCallbacks(ev, callbacks);
  });
  return { parse };
}

/** POST /api/admin/data-sources/:id/excel/append — 추가 업로드 적용. */
export async function streamExcelAppend(
  dataSourceId: string,
  body: AppendExcelMappingRequest,
  callbacks: ExcelStreamCallbacks = {},
): Promise<ExcelCreateStreamResult> {
  const res = await fetch(`/api/admin/data-sources/${dataSourceId}/excel/append`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal: callbacks.signal,
  });
  let resultDsId: string | null = null;
  let sheets: ExcelIngestSheetResult[] = [];
  await readSse(res, (ev) => {
    if (ev.type === 'done') {
      resultDsId = ev.dataSourceId;
      sheets = ev.sheets;
      return;
    }
    dispatchCommonCallbacks(ev, callbacks);
  });
  return { dataSourceId: resultDsId, sheets, skillId: null };
}
